import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'

import { useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { NextData } from 'components/features/accountcreation/NewAccountMain'
import NewAccountVerifyYourEmailAddressForm from 'components/organisms/NewAccountVerifyYourEmailAddressForm'
import {
  START_EMAIL_VERIFICATION,
  COMPLETE_EMAIL_VERIFICATION,
  StartEmailVerificationResponse,
  CompleteEmailVerificationResponse,
  CompleteEmailVerificationVariables
} from 'share/mutations/accountcreation'
import { obfuscateEmailAddress } from 'share/utils/utils'
import {
  AccountCreationError,
  getCorrectError
} from 'share/utils/codeVerificationUtils'
import { AuthVerifyContainer } from 'components/atoms/Layout'

interface NewAccountVerifyYourEmailAddressProps {
  codeValue: string
  nextStep(data: NextData): void
  resendVerificationCode(): void
  setCode: Dispatch<SetStateAction<string>>
  setError(error: AccountCreationError): void
  verifyPhone: boolean
  verifyEmail: boolean
}

const NewAccountVerifyYourEmailAddress: FC<NewAccountVerifyYourEmailAddressProps> =
  ({
    codeValue,
    nextStep,
    resendVerificationCode,
    setCode,
    setError,
    verifyPhone,
    verifyEmail
  }) => {
    const authentication = useAppSelector((state) => state.authentication)
    const [obfuscatedEmailAddress, setObfuscatedEmailAddress] = useState('')
    const [enteredCodeComplete, setEnteredCodeComplete] = useState(false)

    const [startEmailVerification] =
      useMutation<StartEmailVerificationResponse>(START_EMAIL_VERIFICATION)

    const [completeEmailVerification, { loading }] = useMutation<
      CompleteEmailVerificationResponse,
      CompleteEmailVerificationVariables
    >(COMPLETE_EMAIL_VERIFICATION)

    const loadingDelay = useLoadingDelay(loading)

    useEffect(() => {
      startEmailVerificationHelper()
    }, [])

    const startEmailVerificationHelper = () => {
      // call api to send email verification code
      startEmailVerification()
        .then((result) => {
          console.log('startEmailVerification result is', result)
        })
        .catch((e: Error) => {
          setError(AccountCreationError.GeneralError)
          console.log('startEmailVerification failed error is', e)
        })
    }

    const completeEmailVerificationHelper = async (input: string) => {
      try {
        const verificationResult = await completeEmailVerification({
          variables: { code: input }
        })

        // Valid verification code
        if (
          verificationResult.data?.authentication.submitEmailVerificationCode
            .success
        ) {
          setError(AccountCreationError.NoError)
          nextStep({ verifyPhone, verifyEmail })
        }
        // Invalid or expired verification code
        else {
          const responseError =
            verificationResult.data?.authentication.submitEmailVerificationCode
              .error
          const correctError = getCorrectError(responseError)
          setError(correctError)
        }
      } catch (err) {
        setError(AccountCreationError.GeneralError)
      }
    }

    function setObfuscatedEmailAddressHelper(): void {
      let obfuscatedEmailAddressValue = ''
      if (authentication) {
        if (authentication.account) {
          if (authentication.account.emailAddress) {
            const emailAddress = authentication.account.emailAddress
            obfuscatedEmailAddressValue = obfuscateEmailAddress(emailAddress)
          }
        }
      }
      setObfuscatedEmailAddress(obfuscatedEmailAddressValue)
    }

    useEffect(() => {
      setObfuscatedEmailAddressHelper()
    }, [authentication])

    return (
      <AuthVerifyContainer>
        {loadingDelay && loadingSpinnerPortal()}
        <h1 data-testid='create-account-account-verify-email-header'>
          Verify your email address.
        </h1>
        <span
          className='verify-message-text'
          data-testid='create-account-account-verify-email-message'
        >
          A verification code has been sent to your email address,{' '}
          <strong data-testid='create-account-account-verify-email-obfuscated-email'>
            {obfuscatedEmailAddress}
          </strong>
          .
        </span>
        <NewAccountVerifyYourEmailAddressForm
          completeEmailVerificationHelper={completeEmailVerificationHelper}
          enteredCode={codeValue}
          enteredCodeComplete={enteredCodeComplete}
          nextStep={nextStep}
          resendVerificationCode={resendVerificationCode}
          setEnteredCode={setCode}
          setEnteredCodeComplete={setEnteredCodeComplete}
        />
      </AuthVerifyContainer>
    )
  }

export default NewAccountVerifyYourEmailAddress
